import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, AuthContextType } from '@/types'
import { apiService } from '@/services/api'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && !!token
  const isAdmin = user?.role === 'admin'

  const initializeAuth = async () => {
    try {
      const storedToken = localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('user_data')

      if (storedToken && storedUser) {
        setToken(storedToken)
        const userData = JSON.parse(storedUser)
        setUser(userData)
        
        // Verify token is still valid by fetching current user
        try {
          const currentUser = await apiService.getCurrentUser()
          setUser(currentUser)
          localStorage.setItem('user_data', JSON.stringify(currentUser))
        } catch (error) {
          console.error('Token validation failed:', error)
          logout()
        }
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      logout()
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true)
      const tokenResponse = await apiService.login({ email, password })
      
      // Store token
      localStorage.setItem('auth_token', tokenResponse.access_token)
      setToken(tokenResponse.access_token)
      
      // Fetch user data
      const userData = await apiService.getCurrentUser()
      localStorage.setItem('user_data', JSON.stringify(userData))
      setUser(userData)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = (): void => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    setToken(null)
    setUser(null)
  }

  // Listen for logout events from API interceptor
  useEffect(() => {
    const handleLogout = () => {
      logout()
    }
    
    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [])

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth()
  }, [])

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated,
    isAdmin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}